import React, { useState, useCallback, useMemo } from 'react';
import { getAssessmentAssignments } from '../api/assessments-api';

const AssessmentsDataContext = React.createContext(undefined);

function AssessmentsDataProvider({ children }) {
  const [wmAssessments, setWmAssessments] = useState([]);
  const [selfPacedAssessments, setSelfPacedAssessments] = useState([]);
  const [selfCompetenciesAssessments, setSelfCompetenciesAssessments] = useState([]);
  const [raterCompetenciesAssessments, setRaterCompetenciesAssessments] = useState([]);
  const [assigned, setAssigned] = useState('');
  const [assignedAssessments, setAssignedAssessments] = useState({});
  const [assessmentsRetrieved, setAssessmentsRetrieved] = useState(false);
  const [pageQuestionsAnswered, setPageQuestionsAnswered] = useState(false);
  const [pagedQuestions, setPagedQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  const getAssessments = useCallback((auth) => {
    getAssessmentAssignments(auth).then((assessmentData) => {
      setSelfPacedAssessments(assessmentData.selfPaced);
      setWmAssessments(assessmentData.workingMemory);
      setSelfCompetenciesAssessments(assessmentData.selfCompetencies);
      setRaterCompetenciesAssessments(assessmentData.raterCompetencies);
      setAssigned(assessmentData.assigned);
      setAssignedAssessments(assessmentData.assignedAssessments);
      setAssessmentsRetrieved(true);
    });
  }, []);

  const userAssessmentsCompleted = useCallback(() => {
    if (assessmentsRetrieved) {
      if (wmAssessments && wmAssessments.length > 0) {
        return false;
      }
      if (selfPacedAssessments && selfPacedAssessments.length > 0) {
        return false;
      }
      if (selfCompetenciesAssessments && selfCompetenciesAssessments.length > 0) {
        return false;
      }
      return !(raterCompetenciesAssessments && raterCompetenciesAssessments.length > 0);
    }

    return false;
  }, [
    wmAssessments,
    selfPacedAssessments,
    selfCompetenciesAssessments,
    raterCompetenciesAssessments,
    assessmentsRetrieved,
  ]);

  const userAssessmentsAssigned = useCallback(() => {
    if (assessmentsRetrieved) {
      return (
        assignedAssessments.selfPaced ||
        assignedAssessments.workingMemory ||
        assignedAssessments.selfCompetencies ||
        assignedAssessments.raterCompetencies
      );
    }

    return false;
  }, [assignedAssessments, assessmentsRetrieved]);

  const checkAnswers = useCallback((pageQuestions) => {
    for (let q = 0; q < pageQuestions.length; q += 1) {
      const question = pageQuestions[q];
      const questionType = question.question.anchoring.type;
      if (questionType === 'text') {
        if (!question.answer || question.answer.length < 1) {
          setPageQuestionsAnswered(false);
          return;
        }
      } else {
        if (!question.answer) {
          setPageQuestionsAnswered(false);
          return;
        }
      }
      setPageQuestionsAnswered(true);
    }
  }, []);

  const checkAnswersForPage = useCallback(
    (pageNumber) => {
      const pageCheck = pageNumber !== undefined ? pageNumber : currentPage;
      checkAnswers(pagedQuestions[pageCheck]);
    },
    [checkAnswers, currentPage, pagedQuestions]
  );

  const values = useMemo(
    () => ({
      selfPacedAssessments,
      wmAssessments,
      selfCompetenciesAssessments,
      raterCompetenciesAssessments,
      assigned,
      assignedAssessments,
      assessmentsRetrieved,
      pagedQuestions,
      pageQuestionsAnswered,
      setPagedQuestions,
      currentPage,
      setCurrentPage,
      getAssessments,
      checkAnswers,
      checkAnswersForPage,
      userAssessmentsCompleted,
      userAssessmentsAssigned,
    }),
    [
      selfPacedAssessments,
      wmAssessments,
      selfCompetenciesAssessments,
      raterCompetenciesAssessments,
      assigned,
      assignedAssessments,
      assessmentsRetrieved,
      pagedQuestions,
      pageQuestionsAnswered,
      currentPage,
      getAssessments,
      checkAnswers,
      checkAnswersForPage,
      userAssessmentsCompleted,
      userAssessmentsAssigned,
    ]
  );
  return <AssessmentsDataContext.Provider value={values}>{children}</AssessmentsDataContext.Provider>;
}

export const useAssessmentsDataContext = () => {
  const context = React.useContext(AssessmentsDataContext);

  if (context === undefined) {
    throw new Error('useDataContext should be used within an DataProvider.');
  }
  return context;
};

export default AssessmentsDataProvider;
